import {i18n} from '@online-offer/i18n';
import {Grid, GridItem, HStack, Show, ThemeIconKey, useDevice} from 'platform/foundation';
import {formatNumber, useLocale} from 'platform/locale';
import {match} from 'ts-pattern';

import {always, isNotNil, any, values} from 'ramda';
import {isNilOrEmpty, isNotNilOrEmpty} from 'ramda-adjunct';

import {isURLDangerous, Nullish} from 'shared/web';

import {Badge} from '../../components/Badge/Badge';
import {Section} from '../../components/Section/Section';
import {useGetOnlineOfferData} from '../../hooks/useGetOnlineOfferData';
import {canShowCebiaAutotracer} from '../CebiaAutotracer/utils/canShowCebiaAutotracer';
import {SpecificationItem} from './components/SpecificationItem';
import {getBadgeLetter} from './utils/getBadgeLetter';
import {getSpecification} from './utils/getSpecification';

const KwToHPMultiplier = 1.35962;

const convertKwToHP = (kw: number): number => Math.round(kw * KwToHPMultiplier);

const formatPowerWithHorsepower = (kw: number): string => {
  const hp = convertKwToHP(kw);
  return `${kw} ${i18n.t('metricKw')} / ${hp} ${i18n.t('metricHp')}`;
};

function GridSpecificationItem({
  icon,
  categoryTitle,
  value,
}: {
  value: string | Nullish;
  categoryTitle: string;
  icon: ThemeIconKey;
}) {
  if (isNilOrEmpty(value)) {
    return null;
  }
  return (
    <GridItem>
      <SpecificationItem value={value!} categoryTitle={categoryTitle} icon={icon} />
    </GridItem>
  );
}

export function Specification() {
  const {vehicle, alphaCatalogue, carPassDocumentPublicUrl, cebiaSmartCodeUrl} =
    useGetOnlineOfferData();
  const device = useDevice();
  const locale = useLocale();

  if (!vehicle) {
    return null;
  }

  const specificationData = getSpecification(vehicle, alphaCatalogue);

  if (!specificationData || !any(isNotNilOrEmpty, values(specificationData))) {
    return null;
  }

  const {
    bodywork,
    mileage,
    power,
    differential,
    transmission,
    fuel,
    co2Emissions,
    yearOfManufacture,
  } = specificationData;

  const columns = match(device)
    .with('mobile', always(2))
    .with('tablet', always(3))
    .with('notebook', always(3))
    .otherwise(always(4));

  const mileageValue = isNotNil(mileage)
    ? `${formatNumber(locale.localeConfig, mileage, 0)} ${i18n.t('metricKm')}`
    : null;

  const powerValue = isNotNil(power) ? formatPowerWithHorsepower(power) : null;

  const badgeLetter = getBadgeLetter(co2Emissions);

  const co2EmissionsValue = isNotNil(co2Emissions)
    ? `${co2Emissions} ${i18n.t('metricGkm')}`
    : null;

  const yearOfManufactureValue = yearOfManufacture?.split('-')?.[0];

  // Must match the CarPass render guard (nil/empty OR dangerous URL -> no widget), otherwise
  // dropping the break line here would leave the next section without a divider.
  const hasCarPass =
    isNotNilOrEmpty(carPassDocumentPublicUrl) && !isURLDangerous(carPassDocumentPublicUrl);

  // Each banner between Specification and Details draws its own trailing separator,
  // so the break line here is only needed when none of them renders.
  const hasBannerBelow = hasCarPass || canShowCebiaAutotracer(cebiaSmartCodeUrl);

  return (
    <Section id="SPECIFICATION" isWithBreakLine={!hasBannerBelow}>
      <Grid columns={columns} verticalSpacing={[8, 8, 8, 10]}>
        <GridSpecificationItem
          value={yearOfManufactureValue}
          categoryTitle={i18n.t('specification.item.year')}
          icon="specification/year"
        />
        <GridSpecificationItem
          value={mileageValue}
          categoryTitle={i18n.t('specification.item.mileage')}
          icon="specification/mileage"
        />
        <GridSpecificationItem
          value={bodywork}
          categoryTitle={i18n.t('specification.item.vehicle')}
          icon="specification/vehicle"
        />
        <GridSpecificationItem
          value={differential}
          categoryTitle={i18n.t('specification.item.driveType')}
          icon="specification/drive_type"
        />
        <GridSpecificationItem
          value={transmission}
          categoryTitle={i18n.t('specification.item.transmission')}
          icon="specification/transmission"
        />
        <GridSpecificationItem
          value={fuel}
          categoryTitle={i18n.t('specification.item.fuelType')}
          icon="specification/fuel_type"
        />
        <GridSpecificationItem
          value={powerValue}
          categoryTitle={i18n.t('specification.item.power')}
          icon="specification/power"
        />
        <Show when={isNotNilOrEmpty(co2EmissionsValue)}>
          <GridItem>
            <HStack align="flex-end" spacing={[1, 2]}>
              <SpecificationItem
                value={co2EmissionsValue!}
                categoryTitle={i18n.t('specification.item.co2')}
                icon="specification/co2"
              />
              <Show when={isNotNil(badgeLetter)}>
                <Badge variant={badgeLetter!} />
              </Show>
            </HStack>
          </GridItem>
        </Show>
      </Grid>
    </Section>
  );
}
